import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { getSignInPath } from '@/router';
import { ApiError, TokenizedPaymentCard } from '@/models';

import { CountryCode } from '@/store/ui';
import { AccountPageLoadEvent } from '@/services/analytics/events';
import { isCardPaymentProviderInCountry } from '@/helpers';
import { AutoAccrualTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { ErrorEvent } from '@/services/analytics/events';

@Component({
  name: 'CmgPaymentsAndGiftCardsPage',
})
export default class CmgPaymentsAndGiftCardsPage extends Vue {
  autoAccrueStates: Record<number, boolean> = {};
  isLoading = true;
  errorMessage = '';
  providerId: string = '';
  private profileWatcher?: () => void;
  private adobeFlagWatcher?: () => void;
  private paymentCardsWatcher?: () => void;

  async mounted() {
    try {
      const isLoggedIn = await this.$services.customer.waitForCustomerInitialization();
      if (isLoggedIn) {
        await this.$services.transaction.refreshPaymentCards();
        this.syncAutoAccrueStatesFromPaymentCards();
        this.providerId = (await this.$services.payment.getPaymentProvider(this.$appStore.state.ui.countryProfile.countryCode) || '');
        
        this.setAdobeFlagWatcher();
        if (this.isLoyaltyAutoAccrualEnabled) {
          this.initAutoAccrual();
        }

      } else {
        this.$router.push(getSignInPath(this.$route.path));
      }
    } catch (err) {
      // TODO-arwss5: Handle error in UI
    } finally {
      this.isLoading = false;
    }

    this.$services.transaction.clearGiftCardBalanceCache();
  }

  destroyed() {
    if (this.profileWatcher) {
      this.profileWatcher();
    }

    if (this.adobeFlagWatcher) {
      this.adobeFlagWatcher();
    }

    if (this.paymentCardsWatcher) {
      this.paymentCardsWatcher();
    }
  }

  get creditCards(): TokenizedPaymentCard[] {
    return this.$appStore.state.customer.wallet.paymentCards.filter(card => 
      card.isGiftCard == false && isCardPaymentProviderInCountry(this.$appStore.state.ui.countryProfile.countryCode, card.paymentProviderId!));
  }

  get giftCards(): TokenizedPaymentCard[] {
    return this.$appStore.state.customer.wallet.paymentCards.filter(card => card.isGiftCard == true);
  }

  get addACardText(): string {
    return this.creditCards.length == 0 ? 'add a card' : 'add another card';
  }

  get hasReachedMaxCreditCards(): boolean {
    return this.$services.customer.isCustomerAtMaxCreditCards(this.providerId);
  }

  get hasReachedMaxGiftCards(): boolean {
    return this.$services.customer.isCustomerAtMaxGiftCards();
  }

  get areGiftCardsSupported(): boolean {
    return this.$appStore.state.ui.countryProfile.countryCode == 'US';
  }

  get isAboveStorePaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus) {
      if (!flags.orderingStatus.aboveStorePaymentOnline) {
        return false;
      }
    
      if (this.siteCountryCode == CountryCode.US && !(this.isPayeezyPaymentOnline || this.isUComPaymentOnline)) {
        return false;
      }
  
      if (this.siteCountryCode == CountryCode.CA && !this.isChasePaymentOnline) {
        return false;
      }

      if(this.siteCountryCode === CountryCode.GB && !this.isChaseUKPaymentOnline) {
        return false;
      }
    } 

    return true;
  }

  get isPayeezyPaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isPayeezyPaymentOnline) {
      return false;
    }

    return true;
  }

  get isChasePaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isChasePaymentOnline) {
      return false;
    }

    return true;
  }

  get isChaseUKPaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isChaseUKPaymentOnline) {
      return false;
    }

    return true;
  }  

  get isUComPaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.isUComPaymentOnline) {
      return false;
    }

    return true;
  }  

  get siteCountryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get hasSeenScanlessIntroModal(): boolean {
    return !!this.$appStore.state.customer.profile?.hasSeenScanlessIntroModal;
  }

  get hasAnyPaymentCardWithLoyaltyAutoAccrual(): boolean {
    return this.$appStore.state.customer.wallet.paymentCards.some(card => card.loyaltyAutoAccrual != null && card.loyaltyAutoAccrual.eligible === true);
  }

  get autoAccrualInfoIconUrl(): string {
    return this.$appStore.state.cms.images.icons.infoIcon?.default
      // || this.$appStore.state.cms.images.icons.infoOrangeBrown?.default
      || this.$appStore.state.cms.images.icons.info?.default
      || '';
  }

  get isLoyaltyAutoAccrualEnabled(): boolean {
    return (this.$appStore.state.adobeTarget?.loyaltyAutoAccrualEnabled ?? false)
      && (this.$appStore.state.customer.loyalty?.isEnrolled ?? false);
  }

  get accountPageLoadEventType(): string {
    if (this.isLoyaltyAutoAccrualEnabled && !this.hasSeenScanlessIntroModal && this.hasAnyPaymentCardWithLoyaltyAutoAccrual) {
      return AutoAccrualTrackingType.EnrollInAutoAccrual;
    }

    return 'payments';
  }

  async onClickAddACreditCard(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('addCreditOrDebitCardModal', true);
    this.$services.analytics.send(new AccountPageLoadEvent('add-credit-card'));
    this.$services.ui.firstFocus();
    
  }

  onClickAddAGiftCard(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('addGiftCardModal', true);
    this.$services.analytics.send(new AccountPageLoadEvent('add-gift-card'));
    this.$services.ui.firstFocus();
  }

  onClickAutoAccrualInfoIcon(): void {
    this.$services.analytics.send(new AccountPageLoadEvent(AutoAccrualTrackingType.AutoAccrualInfo));
    this.$services.ui.setModalVisibility('scanlessInfoIconModal', true);
  }

  private setAdobeFlagWatcher(): void {
    this.adobeFlagWatcher = this.$appStore.watch(
      state => state.adobeTarget.loyaltyAutoAccrualEnabled,
      () => {
        // If isLoyaltyAutoAccrualEnabled is not set to true before component is mounted, ensure initAutoAccrual is called when it becomes true
        // getScanlessCreditCardAlreadyAssociated will be undefined if initAutoAccrual has not been called
        if(!this.$services.config.getScanlessCreditCardAlreadyAssociated() && this.isLoyaltyAutoAccrualEnabled) {
          this.initAutoAccrual();
        }
      }
    )
  }

  private setScanlessIntroductionWatchers(): void {
    this.profileWatcher = this.$appStore.watch(
      state => state.customer.profile,
      () => {
        if (this.isLoyaltyAutoAccrualEnabled) {
          this.showScanlessIntroductionModalIfEligible();
        }
      },
    );

    this.paymentCardsWatcher = this.$appStore.watch(
      state => state.customer.wallet.paymentCards,
      () => {
        this.syncAutoAccrueStatesFromPaymentCards();
        if (this.isLoyaltyAutoAccrualEnabled) {
          this.showScanlessIntroductionModalIfEligible();
        }
      },
    );
  }

  private syncAutoAccrueStatesFromPaymentCards(): void {
    const mappedStates: Record<number, boolean> = {};

    this.$appStore.state.customer.wallet.paymentCards.forEach(card => {
      if (card.tokenId === undefined) {
        return;
      }

      mappedStates[card.tokenId] = !!card.loyaltyAutoAccrual && card.loyaltyAutoAccrual.enrolled === true;
    });

    this.autoAccrueStates = mappedStates;
  }

  private async initAutoAccrual(): Promise<void> {
    await this.$services.config.setScanlessIntroductionModal();
    await this.$services.config.setScanlessInfoIconModal();
    await this.$services.config.setScanlessCreditCardAlreadyAssociated();

    this.$services.analytics.send(new AccountPageLoadEvent(this.accountPageLoadEventType));
    this.setScanlessIntroductionWatchers();          
    this.showScanlessIntroductionModalIfEligible();
  }

  private showScanlessIntroductionModalIfEligible(): void {
    if (!this.$services.customer.isLoggedIn()) {
      return;
    }

    if (!this.isLoyaltyAutoAccrualEnabled) {
      return;
    }

    if (this.hasSeenScanlessIntroModal) {
      return;
    }

    if (!this.hasAnyPaymentCardWithLoyaltyAutoAccrual) {
      return;
    }

    // Feature is gated by the loyalty-auto-accrual-flag target decision scope.
    // Show the modal only if the flag is enabled.

    const customerProfile = this.$appStore.state.customer.profile;
    if (customerProfile) {
      customerProfile.hasSeenScanlessIntroModal = true;
      this.$services.customer.updateCustomer({ hasSeenScanlessIntroModal: true });
    }

    this.$services.ui.setModalVisibility('scanlessIntroModal', true);
  }

  private isAutoAccrueEligible(card: TokenizedPaymentCard): boolean {
    return this.isLoyaltyAutoAccrualEnabled && card.loyaltyAutoAccrual != null && card.loyaltyAutoAccrual.eligible === true;
  }

  private getAutoAccrualEventSuffix(enrolled: boolean, eventPhase: 'toggle' | 'confirmation'): string {
    if (eventPhase === 'toggle') {
      return enrolled ? AutoAccrualTrackingType.AutoAccrualOn : AutoAccrualTrackingType.AutoAccrualOff;
    } else {
      return enrolled ? AutoAccrualTrackingType.AutoAccrualActivated : AutoAccrualTrackingType.AutoAccrualDeactivated;
    }
  }

  private isAutoAccrue(tokenId: number | undefined): boolean {
    return tokenId !== undefined ? !!this.autoAccrueStates[tokenId] : false;
  }

  displayAutoAccrueGenericError(): void {
    // send error event and display generic error
    this.$services.analytics.applyOne({
      error: {
        errors: { "value": 1 },
        errorName: "account|We encountered an error and were not able to enroll you in auto accrual. Please try again.",
        errorType: "account",
        errorMessage: "We encountered an error and were not able to enroll you in auto accrual. Please try again.",
      }
    });
    this.$services.analytics.send(new ErrorEvent('account', 'We encountered an error and were not able to enroll you in auto accrual. Please try again.', ''));

    this.$services.ui.setModalVisibility('autoAccrualErrorModal', true, {
      header: "SOMETHING WENT WRONG",
      content: "We encountered an error and were not able to enroll you in auto accrual. Please try again.",
      options: [
        {
          text: 'Okay',
          type: ButtonType.Primary,
          onClick: () => this.$services.ui.setModalVisibility('autoAccrualErrorModal', false),
        }
      ]
    });
  }

  toggleAutoAccrue(tokenId: number | undefined): void {
    if (tokenId === undefined) return;

    // Find the card and check if it's eligible
    const card = this.$appStore.state.customer.wallet.paymentCards.find(c => c.tokenId === tokenId);
    if (!card || !this.isAutoAccrueEligible(card)) {
      return;
    }

    const newValue = !this.autoAccrueStates[tokenId];
    this.$set(this.autoAccrueStates, tokenId, newValue);

    // Send toggle event
    this.$services.analytics.send(new AccountPageLoadEvent(this.getAutoAccrualEventSuffix(newValue, 'toggle')));

    // Call the API to update the enrollment status
    this.$services.transaction.updateLoyaltyAutoAccrualEnrolled(tokenId, newValue)
      .then(() => {
        const message = newValue
          ? "You've turned on auto accrual on this card"
          : "You've turned off auto accrual on this card";

        this.$services.ui.setMessageBannerState({
          message,
          visible: true,
          timeoutMs: 4000,
        });

        this.$services.analytics.send(new AccountPageLoadEvent(this.getAutoAccrualEventSuffix(newValue, 'confirmation')));
      })
      .catch((error) => {
        // Revert the local state if the API call fails
        this.$set(this.autoAccrueStates, tokenId, !newValue);
        this.$services.logger.error('Failed to update loyalty auto accrual enrollment', error);

        if (error.response && error.response.data && error.response.data.errorCode === 'TRN-0001') {
          const cardAlreadyAssociatedFragment = this.$services.config.getScanlessCreditCardAlreadyAssociated();
          
          if (cardAlreadyAssociatedFragment && cardAlreadyAssociatedFragment.isModalEnabled) { 
            // send analytics and display credit card already associated error modal
            this.$services.analytics.applyOne({
              error: {
                errors: { "value": 1 },
                errorName: "account|This Card is Already Linked To An Account",
                errorType: "account",
                errorMessage: "This Card is Already Linked To An Account",
              }
            });
            this.$services.analytics.send(new ErrorEvent('account', 'This Card is Already Linked To An Account', ''));

            this.$services.ui.setModalVisibility('autoAccrualErrorModal', true, {
              header: cardAlreadyAssociatedFragment.title,
              content: cardAlreadyAssociatedFragment.body,
              options: [
                {
                  text: 'Okay',
                  type: ButtonType.Primary,
                  onClick: () => this.$services.ui.setModalVisibility('autoAccrualErrorModal', false),
                }
              ]
            });
          } else {
            // default to generic error if GQL call fails
            this.displayAutoAccrueGenericError();
          }
        } else {
          this.displayAutoAccrueGenericError();
        }
      });
  }
}
